import { useMemo, useState, type CSSProperties } from 'react';
import {
  Card, Typography, Button, Modal, Form, Select, Checkbox, message, Tag, Tooltip, theme, EmptyState,
  Popconfirm, Spinner, Tree, CodeChip, type TagTone,
} from '../../ui';
import {
  UserPlus, Star, AlertTriangle, ArrowLeftRight,
  Split, Merge, UserCog,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { useAuthStore } from '../../store/auth';
import { staffOption, staffText, StaffNote } from '../../lib/staffLabel';
import { RoleBadge, rolesOfUser } from '../../components/RoleBadge';
import { TransferTerritoryModal } from './TransferTerritoryModal';
import { CoverageManagerCard } from './CoverageManagerCard';
import { UnassignedPlacesCard, type PlaceOversight } from './UnassignedPlacesCard';
import { SplitTerritoryModal } from './SplitTerritoryModal';
import { MergeTerritoriesModal } from './MergeTerritoriesModal';
import { SHTransferModal } from './SHTransferModal';
import { CoverageFilters } from './CoverageFilters';
import { PALETTE } from '../../lib/palette';

type Level = 'zone' | 'state' | 'district' | 'beat';
const LEVEL_META: Record<Level, { label: string; color: string }> = {
  zone: { label: 'Zone', color: 'purple' },
  state: { label: 'State', color: 'blue' },
  district: { label: 'District', color: 'cyan' },
  beat: { label: 'Beat', color: 'green' },
};
// Which role may be bound at each level (mirrors the backend LEVELS_FOR_ROLE).
// A Sales Head oversees state/district/beat; a Sales Person works district/beat. Zone is
// a navigation container only — nobody is bound at zone level.
const SP_LEVELS: Level[] = ['district', 'beat'];
const SH_LEVELS: Level[] = ['state', 'district', 'beat'];

interface CoverageRow {
  assignmentId: string; territoryId: string; userId: string;
  kind: string; isPrimary: number; fullName: string; roleCode: string;
}

interface AssignTarget { territory: any; assignedUserIds: string[] }

// Find a node by id anywhere in the territory tree.
function findNode(nodes: any[], id?: string): any {
  if (!id) return null;
  for (const n of nodes) { if (String(n.id) === String(id)) return n; const f = findNode(n.children ?? [], id); if (f) return f; }
  return null;
}

/**
 * The ⭐ on a chip: hollow = covers the territory, filled gold = the primary owner (main responsible,
 * gets new customers). If the viewer may assign this role it's clickable: click a hollow star to make
 * them owner (with a confirm when it displaces the current owner), click a filled star to clear it.
 * Ownership is just this annotation + who NEW customers route to — every coverer still sees all customers.
 */
function OwnerStar({ row, canStar, territoryName, otherOwner, onSet }: {
  row: CoverageRow; canStar: boolean; territoryName: string;
  otherOwner?: CoverageRow; onSet: (isPrimary: boolean) => void;
}) {
  const owner = !!row.isPrimary;
  const style: CSSProperties = { display: 'inline-flex', marginInlineEnd: 4, cursor: canStar ? 'pointer' : 'default', opacity: owner || canStar ? 1 : 0.5 };
  const icon = <Star size={16} style={{ verticalAlign: '-2px' }} fill={owner ? PALETTE.harvest[400] : 'none'} color={owner ? PALETTE.harvest[400] : 'currentColor'} />;
  const stop = (e: any) => e.stopPropagation();
  if (!canStar) {
    return <span style={style} title={owner ? 'Main responsible — owns new customers here' : 'Covers this territory'}>{icon}</span>;
  }
  if (owner) {
    return (
      <Popconfirm placement="top"
        title={`Remove ${row.fullName} as owner of ${territoryName}?`}
        description="New customers here won’t auto-route until you set an owner. Existing customers are untouched."
        okText="Clear owner" onConfirm={() => onSet(false)}>
        <span style={style} title="Owns new customers here — click to clear" onClick={stop}>{icon}</span>
      </Popconfirm>
    );
  }
  if (otherOwner) {
    return (
      <Popconfirm placement="top"
        title={`Make ${row.fullName} the owner of ${territoryName}?`}
        description={`${otherOwner.fullName} keeps their existing customers but stops getting new ones here.`}
        okText="Make owner" onConfirm={() => onSet(true)}>
        <span style={style} title="Click to make main responsible (owns new customers)" onClick={stop}>{icon}</span>
      </Popconfirm>
    );
  }
  // No current owner on this territory → one click, no confirm.
  return (
    <span style={style} title="Click to make main responsible (owns new customers)"
      onClick={(e) => { stop(e); onSet(true); }}>{icon}</span>
  );
}

/** Prune a territory tree to nodes in `domain` (the actor's expanded set), keeping
 *  ancestors of any kept node so the hierarchy path stays visible. */
function keepDomain(nodes: any[], domain: Set<string>): any[] {
  const keep = (n: any): any | null => {
    const kids = (n.children ?? []).map(keep).filter(Boolean);
    if (domain.has(String(n.id)) || kids.length) return { ...n, children: kids };
    return null;
  };
  return nodes.map(keep).filter(Boolean);
}

export function TerritoryCoveragePage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const canAssignSp = usePermission('TERRITORY.ASSIGN');
  const canAssignSh = usePermission('TERRITORY.ASSIGN.HEAD');
  const canTransfer = usePermission('TERRITORY.TRANSFER');
  const canCoverage = usePermission('TERRITORY.COVERAGE');
  const canSplitMerge = usePermission('TERRITORY.SPLIT_MERGE');
  const qc = useQueryClient();

  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ territory: any; currentPrimary: { userId: string; fullName: string } | null } | null>(null);
  const [splitTarget, setSplitTarget] = useState<any | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [shTransferOpen, setShTransferOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scopePath, setScopePath] = useState<string[]>();
  const [personId, setPersonId] = useState<string>();

  // Admins see the whole tree; a Sales Head/Person sees only their own domain so the KPIs
  // and gap marks reflect what they actually manage (coverage chips are already
  // backend-scoped). me/territories returns the actor's expanded set (all active for admins).
  const myRole = useAuthStore((s) => s.user?.role);
  const isAdminView = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS'].includes(myRole ?? '');

  const { data: tree, isLoading } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
  });
  const { data: coverage } = useQuery({
    queryKey: ['territory-coverage'],
    queryFn: () => request<CoverageRow[]>({ url: '/territory-assignments' }),
  });
  const { data: myTerritories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: !isAdminView,
  });
  const mySet = useMemo(() => new Set((myTerritories ?? []).map((t: any) => String(t.id))), [myTerritories]);

  // The tree to actually work with — pruned to the actor's domain for non-admins.
  const scopedTree = useMemo(() => (isAdminView ? (tree ?? []) : keepDomain(tree ?? [], mySet)), [tree, isAdminView, mySet]);

  // territoryId → assigned users.
  const byTerritory = useMemo(() => {
    const m = new Map<string, CoverageRow[]>();
    for (const c of coverage ?? []) {
      const k = String(c.territoryId);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    }
    return m;
  }, [coverage]);

  // Two DIFFERENT gaps, deliberately not sharing one label — the old single "Unassigned" tag read as a
  // flat contradiction on a node that plainly had a person's chip next to it (a Sales Head bound to
  // MH-NANDED with no ⭐ still showed "Unassigned"):
  //   • worked   — a Sales Person, or a Sales Head who OWNS it (is_primary), on the node or an ancestor.
  //                This is exactly what the backend counts, so it is what the badge is made of.
  //   • overseen — any non-coverage binding on the node or an ancestor, INCLUDING a Sales Head who is
  //                only watching (no ⭐). Overseen-but-not-worked is a real state: someone is
  //                accountable for the area, nobody works it. That reads "No sales person", not
  //                "Unassigned", and its fix is one click on that head's ☆.
  // Computed for EVERY level, so the filter/tag applies to states and districts too, not only beats.
  // (Zones are containers and are never flagged.)
  const { workedIds, overseenVia } = useMemo(() => {
    const workedHere = new Set<string>();
    const boundHere = new Map<string, CoverageRow[]>();
    for (const c of coverage ?? []) {
      if (c.kind === 'COVERAGE') continue;
      const k = String(c.territoryId);
      if (!boundHere.has(k)) boundHere.set(k, []);
      boundHere.get(k)!.push(c);
      if (c.roleCode === 'SALES_PERSON' || (c.roleCode === 'SALES_HEAD' && c.isPrimary)) workedHere.add(k);
    }
    const worked = new Set<string>();
    // node id → WHO reaches it and from WHERE. `territory` is null when the binding sits on the node
    // itself; otherwise it names the ancestor the people are actually bound to, so the card can say
    // "watching Balod" rather than implying they are on the beat.
    const via = new Map<string, { territory: string | null; people: CoverageRow[] }>();
    const walk = (node: any, ancestorWorked: boolean, ancestor: { territory: string; people: CoverageRow[] } | null) => {
      const id = String(node.id);
      const here = ancestorWorked || workedHere.has(id);
      if (here) worked.add(id);
      const selfPeople = boundHere.get(id) ?? [];
      if (selfPeople.length) via.set(id, { territory: null, people: selfPeople });
      else if (ancestor) via.set(id, { territory: ancestor.territory, people: ancestor.people });
      const passDown = selfPeople.length ? { territory: node.name, people: selfPeople } : ancestor;
      (node.children ?? []).forEach((c: any) => walk(c, here, passDown));
    };
    scopedTree.forEach((n) => walk(n, false, null));
    return { workedIds: worked, overseenVia: via };
  }, [scopedTree, coverage]);

  // Full ancestor path for a node ("Maharastra › Dhule"), built from the UNPRUNED tree so the
  // worklist card can place a beat even when the viewer's own domain starts below its state.
  const pathById = useMemo(() => {
    const m = new Map<string, string>();
    const walk = (n: any, trail: string[]) => {
      m.set(String(n.id), trail.join(' › '));
      const next = n.level === 'zone' ? trail : [...trail, n.name]; // a zone is a container, not a place
      (n.children ?? []).forEach((c: any) => walk(c, next));
    };
    (tree ?? []).forEach((n: any) => walk(n, []));
    return m;
  }, [tree]);

  // WHO is attached to a flagged place without working it. Named rather than described: the reader's
  // next move is to talk to that person or click their ☆, and neither is possible from a state label.
  const reasonFor = (id: string): PlaceOversight | null => {
    const seen = overseenVia.get(id);
    if (!seen?.people.length) return null;
    const [first, ...rest] = seen.people;
    const names = seen.people.map((p) => p.fullName).join(', ');
    return {
      name: first.fullName,
      roleCode: first.roleCode,
      others: rest.length,
      via: seen.territory,
      why: seen.territory
        ? `${names} ${seen.people.length === 1 ? 'is' : 'are'} bound to ${seen.territory}, above this place, but ${seen.people.length === 1 ? 'does' : 'do'} not work it. Assign a Sales Person here — or click the ☆ on their chip in the tree to make them the owner.`
        : `${names} ${seen.people.length === 1 ? 'is' : 'are'} here for oversight only and ${seen.people.length === 1 ? 'does' : 'do'} not work it. Assign a Sales Person — or click the ☆ on their chip in the tree to make them the owner.`,
    };
  };


  const remove = useMutation({
    mutationFn: (assignmentId: string) => request({ url: `/territory-assignments/${assignmentId}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Assignment removed');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to remove'),
  });

  // One-click ⭐ owner toggle on a chip.
  const setPrimaryMut = useConfirmedMutation((v) => ({ title: v.isPrimary ? "Make this the primary coverage?" : "Remove primary from this coverage?", content: "The primary owner is the one held accountable for the territory.", okText: "Yes" }), {
    mutationFn: ({ id, isPrimary }: { id: string; isPrimary: boolean }) =>
      request<any>({ url: `/territory-assignments/${id}/primary`, method: 'POST', data: { isPrimary } }),
    onSuccess: (res: any) => {
      const claimed = res?.claimed ?? 0;
      message.success(res?.isPrimary
        ? (claimed ? `Owner set — ${claimed} unowned customer(s) handed over` : 'Owner set')
        : 'Owner cleared');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update owner'),
  });

  const canAssignAt = (level: Level) =>
    (canAssignSp && SP_LEVELS.includes(level)) || (canAssignSh && SH_LEVELS.includes(level));

  // Build the antd Tree node, with assignment chips + an "assign" action.
  // Hierarchical Territory picker options (state → district → beat). Starts at States; if more than one
  // zone ever exists, the top zone isn't collapsed, so zones become the first column automatically.
  const terrOptions = useMemo(() => {
    const toOpt = (n: any): any => ({ value: String(n.id), label: n.name, children: n.children?.length ? n.children.map(toOpt) : undefined });
    const start = (scopedTree.length === 1 && scopedTree[0].level === 'zone') ? (scopedTree[0].children ?? []) : scopedTree;
    return start.map(toOpt);
  }, [scopedTree]);
  const personOptions = useMemo(() => {
    const m = new Map<string, { fullName: string; roleCode: string }>();
    for (const c of coverage ?? []) if (!m.has(String(c.userId))) m.set(String(c.userId), { fullName: c.fullName, roleCode: c.roleCode });
    return [...m.entries()].map(([id, u]) => ({ value: id, name: u.fullName, role: (u.roleCode === 'SALES_HEAD' ? 'SH' : 'SP') as 'SH' | 'SP' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [coverage]);
  // Which subtree to render: the picked state/district/beat (last id in the path), else the whole tree.
  const rootsToRender = useMemo(() => {
    const id = scopePath?.length ? scopePath[scopePath.length - 1] : undefined;
    if (id) { const n = findNode(scopedTree, id); return n ? [n] : []; }
    return scopedTree;
  }, [scopedTree, scopePath]);

  // `parentNeedsOwner` marks a node whose gap is INHERITED — the same one its parent already carries.
  // Only the top of a gap gets the alert chip: Chhattisgarh plus its seven districts is ONE gap with
  // one fix, and painting all eight red made the tree unreadable and disagreed with the worklist,
  // which rolls the same gap up to its highest place.
  const convert = (t: any, parentNeedsOwner = false): any => {
    const meta = LEVEL_META[t.level as Level];
    const rows = (byTerritory.get(String(t.id)) ?? []).slice().sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    const primarySp = rows.find((r) => r.isPrimary && r.roleCode === 'SALES_PERSON');
    const needsOwner = t.level !== 'zone' && !workedIds.has(String(t.id));
    const topOfGap = needsOwner && !parentNeedsOwner;
    // Someone IS accountable here (a head watching the node or an ancestor) — just nobody working it.
    const overseenBy = needsOwner ? overseenVia.get(String(t.id)) : undefined;
    const isOverseen = needsOwner && overseenVia.has(String(t.id));
    const gapLabel = isOverseen ? 'No sales person' : 'Nobody assigned';
    const gapWhy = isOverseen
      ? (overseenBy
        ? `Overseen through ${overseenBy}, but no sales person works ${t.name}. Assign one here — or click the ☆ on a Sales Head's chip to make them the owner.`
        : `Someone oversees ${t.name}, but nobody works it. Assign a Sales Person — or click the ☆ on a Sales Head's chip to make them the owner.`)
      : `Nobody is assigned to ${t.name}, at this level or above it. New customers created here get no owner.`;
    const matchesSearch = !search || `${t.code} ${t.name}`.toLowerCase().includes(search.toLowerCase());
    const matchesPerson = !personId || rows.some((r) => String(r.userId) === personId);
    return {
      key: String(t.id),
      _level: t.level,
      _match: matchesSearch,
      _person: matchesPerson,
      title: (
        <div className="gb-cov-node">
          <span className="gb-cov-main">
            <Tag tone={meta?.color as TagTone} className="gb-cov-pill">{meta?.label ?? t.level}</Tag>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{t.code}</b>
            <span className="gb-cov-name">{t.name}</span>
            {topOfGap && (
              <Tooltip label={gapWhy}>
                <Tag tone={isOverseen ? 'gold' : 'red'} style={{ marginInlineStart: 4 }}>
                  <AlertTriangle size={16} />{gapLabel}
                </Tag>
              </Tooltip>
            )}
          </span>
          <span className="gb-cov-chips">
            {rows.map((r) => {
              const roleColor = r.roleCode === 'SALES_HEAD' ? 'blue' : 'green';
              const canStar = (r.roleCode === 'SALES_HEAD' ? canAssignSh : canAssignSp);
              const otherOwner = rows.find((x) => x.isPrimary && String(x.assignmentId) !== String(r.assignmentId));
              return (
                <Tag
                  key={r.assignmentId}
                  tone={roleColor}
                  className="gb-cov-chip"
                >
                  <OwnerStar
                    row={r} canStar={canStar} territoryName={t.name} otherOwner={otherOwner}
                    onSet={(isPrimary) => setPrimaryMut.mutate({ id: r.assignmentId, isPrimary })}
                  />
                  {r.fullName}
                  {canStar && (
                    <Popconfirm
                      title={`Remove ${r.fullName} from ${t.name}?`}
                      okText="Remove" okButtonProps={{ danger: true }}
                      onConfirm={() => remove.mutate(r.assignmentId)}
                    >
                      <span className="gb-cov-x" onClick={(e) => e.stopPropagation()}>×</span>
                    </Popconfirm>
                  )}
                </Tag>
              );
            })}
            {canAssignAt(t.level as Level) && (
              <Tooltip label="Assign a user here">
                <Button
                  variant="default" size="sm" icon={<UserPlus size={16} />}
                  onClick={(e) => { e.stopPropagation(); setAssignTarget({ territory: t, assignedUserIds: rows.map((r: any) => String(r.userId)) }); }}
                >
                  Assign
                </Button>
              </Tooltip>
            )}
            {canTransfer && primarySp && SP_LEVELS.includes(t.level as Level) && (
              <Tooltip label={`Transfer from ${primarySp.fullName} to another SP`}>
                <Button
                  size="sm" icon={<ArrowLeftRight size={16} />}
                  onClick={(e) => { e.stopPropagation(); setTransferTarget({ territory: t, currentPrimary: { userId: String(primarySp.userId), fullName: primarySp.fullName } }); }}
                >
                  Transfer
                </Button>
              </Tooltip>
            )}
            {canSplitMerge && (
              <Tooltip label="Split into a new sibling territory">
                <Button size="sm" icon={<Split size={16} />}
                  onClick={(e) => { e.stopPropagation(); setSplitTarget(t); }}>
                  Split
                </Button>
              </Tooltip>
            )}
          </span>
        </div>
      ),
      children: (t.children ?? []).map((c: any) => convert(c, needsOwner)),
    };
  };

  // Prune the tree to the active filters, keeping ancestors of any matching node.
  const pruneTree = (nodes: any[]): any[] => {
    const keep = (n: any): any | null => {
      const kids = (n.children ?? []).map(keep).filter(Boolean);
      const self = n._match && n._person;
      if (self || kids.length) return { ...n, children: kids };
      return null;
    };
    return nodes.map(keep).filter(Boolean);
  };
  const treeData = pruneTree(rootsToRender.map((n: any) => convert(n)));

  return (
    <div>
      {!embedded && <Typography.Title level={4} style={{ marginBottom: 2 }}>Territory Coverage</Typography.Title>}
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Bind Sales Heads to states, districts or beats for oversight and Sales Persons to districts/beats. A beat can hold
        several SPs — the <Star size={14} style={{ color: 'var(--color-harvest-400)', display: 'inline', verticalAlign: '-2px' }} /> primary owns new customers created there.
      </Typography.Paragraph>

      {/* The two halves the Territories badge is made of, both on this page and both clearable here:
          beats with no sales person, then sales persons away today with no stand-in. */}
      {(canAssignSp || canAssignSh) && (
        <UnassignedPlacesCard
          canAssign={canAssignSp || canAssignSh}
          pathOf={(id) => pathById.get(id) ?? ''}
          reasonOf={reasonFor}
          onAssign={(b) => {
            const node = findNode(tree ?? [], String(b.id)) ?? b;
            setAssignTarget({ territory: node, assignedUserIds: (byTerritory.get(String(b.id)) ?? []).map((r) => String(r.userId)) });
          }}
        />
      )}
      {canCoverage && <CoverageManagerCard />}

      {/* Filters live OUTSIDE the card head — the card head clips (overflow:hidden), which would cut the dropdowns. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CoverageFilters
          search={search} setSearch={setSearch}
          scopePath={scopePath} setScopePath={setScopePath} terrOptions={terrOptions}
          personId={personId} setPersonId={setPersonId} personOptions={personOptions}
        />
        {(canSplitMerge || canAssignSh) && (
          <div className="ml-auto flex flex-wrap gap-2">
            {canSplitMerge && <Button size="sm" icon={<Merge size={16} />} onClick={() => setMergeOpen(true)}>Merge</Button>}
            {canAssignSh && <Button size="sm" icon={<UserCog size={16} />} onClick={() => setShTransferOpen(true)}>Transfer SH</Button>}
          </div>
        )}
      </div>
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>
        ) : treeData.length === 0 && !isLoading ? (
          <EmptyState
            title={
              (search || scopePath?.length || personId) ? 'No territories match the current filters.'
                : 'No territories yet. Create them under Masters → Territories.'} />
        ) : (
          <Tree
            className="gb-cov-tree"
            defaultExpandAll
            showLine={{ showLeafIcon: false }}
            blockNode
            selectable={false}
            treeData={treeData}
          />
        )}
      </Card>

      <AssignModal target={assignTarget} onClose={() => setAssignTarget(null)} />
      <TransferTerritoryModal
        open={!!transferTarget}
        onClose={() => setTransferTarget(null)}
        territory={transferTarget?.territory}
        currentPrimary={transferTarget?.currentPrimary ?? null}
      />
      <SplitTerritoryModal open={!!splitTarget} onClose={() => setSplitTarget(null)} territory={splitTarget} />
      <MergeTerritoriesModal open={mergeOpen} onClose={() => setMergeOpen(false)} />
      <SHTransferModal open={shTransferOpen} onClose={() => setShTransferOpen(false)} />

      <style>{`
        .gb-cov-tree .ant-tree-treenode { padding: 5px 0; align-items: center; width: 100%; }
        .gb-cov-tree .ant-tree-node-content-wrapper { width: 100%; }
        .gb-cov-tree .ant-tree-node-content-wrapper:hover { background: ${token.colorFillTertiary}; }
        .gb-cov-node { display: flex; align-items: center; gap: 10px; padding-right: 8px; flex-wrap: wrap; }
        .gb-cov-main { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
        .gb-cov-pill { margin: 0; font-size: 10px; line-height: 16px; text-transform: uppercase; letter-spacing: .4px; border: none; }
        .gb-cov-name { color: ${token.colorTextSecondary}; }
        .gb-cov-chips { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .gb-cov-chip { margin: 0; }
        .gb-cov-x { margin-inline-start: 6px; cursor: pointer; font-weight: 700; opacity: .65; }
        .gb-cov-x:hover { opacity: 1; }
      `}</style>
    </div>
  );
}

function AssignModal({ target, onClose }: { target: AssignTarget | null; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const myRole = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);

  const open = !!target;
  const t = target?.territory;
  const level: Level | undefined = t?.level;
  const meta = level ? LEVEL_META[level] : null;

  // Role that may be bound at this level. District allows either; the user picks.
  const allowSp = !!level && SP_LEVELS.includes(level);
  const allowSh = !!level && SH_LEVELS.includes(level);
  const role = Form.useWatch('role', form) ?? (allowSp ? 'SALES_PERSON' : 'SALES_HEAD');

  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: open && allowSp,
  });
  const { data: shUsers } = useQuery({
    queryKey: ['sh-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_HEAD' }),
    enabled: open && allowSh,
  });

  // The tree and the coverage map are ALREADY in the page's cache under these keys — read them rather
  // than refetch, so the richer option rows below cost the picker nothing.
  const { data: tree } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
    enabled: open,
  });
  const { data: coverage } = useQuery({
    queryKey: ['territory-coverage'],
    queryFn: () => request<CoverageRow[]>({ url: '/territory-assignments' }),
    enabled: open,
  });

  const terrById = useMemo(() => {
    const m = new Map<string, { code?: string | null; name?: string | null; level?: string }>();
    const walk = (n: any) => { m.set(String(n.id), { code: n.code, name: n.name, level: n.level }); (n.children ?? []).forEach(walk); };
    (tree ?? []).forEach(walk);
    return m;
  }, [tree]);

  // Where the territory being assigned sits in the tree: everything ABOVE it (a binding up there
  // already reaches this node) and everything UNDER it (a binding down there means the person is
  // already on the ground inside it). Both are what make one candidate a better answer than another.
  const { aboveIds, underIds } = useMemo(() => {
    const above = new Set<string>(); const under = new Set<string>();
    const collect = (n: any) => { under.add(String(n.id)); (n.children ?? []).forEach(collect); };
    const walk = (n: any, trail: string[]): boolean => {
      if (String(n.id) === String(t?.id)) { trail.forEach((id) => above.add(id)); (n.children ?? []).forEach(collect); return true; }
      return (n.children ?? []).some((c: any) => walk(c, [...trail, String(n.id)]));
    };
    if (t) (tree ?? []).forEach((n: any) => walk(n, []));
    return { aboveIds: above, underIds: under };
  }, [tree, t]);

  // userId → the territories they are bound to today. A COVERAGE row is a temporary stand-in grant
  // while somebody is away, not a person's own book, so it is not counted as work they carry.
  const bookByUser = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const c of coverage ?? []) {
      if (c.kind === 'COVERAGE') continue;
      const k = String(c.userId);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(String(c.territoryId));
    }
    return m;
  }, [coverage]);

  // `/territory-assignments` answers within the caller's own domain for a Sales Head — so the book
  // this modal can see is the whole truth for an admin and "what you oversee" for a head. The chip's
  // hover says which, rather than a count quietly claiming to be complete.
  const seesWholeTree = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS'].includes(myRole ?? '');

  /**
   * THE ONE STANDING FACT A CANDIDATE'S ROW CARRIES, most decisive first — at most one, because two
   * marks on a row is a puzzle instead of an answer:
   *   works the area above  — assigning them here only makes the reach they already have exact
   *   works inside it       — they are on the ground under this node already
   *   nothing yet           — free capacity, nobody is being stretched
   *   otherwise             — how much they carry today, in the unit the reader works in ("4 beats")
   */
  const factFor = (userId: string) => {
    const book = bookByUser.get(userId) ?? [];
    const named = book.map((id) => ({ id, ...(terrById.get(id) ?? {}) })).filter((x) => x.code);
    const targetWord = (LEVEL_META[t?.level as Level]?.label ?? 'territory').toLowerCase();

    const above = named.find((x) => aboveIds.has(x.id));
    if (above) {
      const word = (LEVEL_META[above.level as Level]?.label ?? 'area').toLowerCase();
      return {
        rank: 0,
        node: <StaffNote tone="gold">works {above.code}</StaffNote>,
        why: `Already covers ${above.name} — the ${word} above this ${targetWord}`,
      };
    }
    const inside = named.filter((x) => underIds.has(x.id) && String(x.id) !== String(t?.id));
    if (inside.length) {
      return {
        rank: 1,
        node: <StaffNote tone="gold">{inside.length === 1 ? `works ${inside[0].code}` : `works ${inside.length} inside`}</StaffNote>,
        why: `Already works inside ${t?.name}: ${inside.map((x) => x.code).join(', ')}`,
      };
    }
    if (!named.length) {
      // Rank 2, the same as the rows below: only "already reaches this territory" earns the top of
      // the list. Holding nothing is worth SAYING — it is not a reason to be offered first.
      return {
        rank: 2,
        node: <StaffNote>no territory yet</StaffNote>,
        why: seesWholeTree ? 'Holds no territory at all — free to take this one' : 'Holds nothing in the areas you oversee',
      };
    }
    // A bare count is only honest if it names its unit: "4" could be beats, districts or customers.
    const levels = new Set(named.map((x) => String(x.level ?? '')));
    const word = levels.size === 1 ? (LEVEL_META[[...levels][0] as Level]?.label ?? 'area').toLowerCase() : 'area';
    const codes = named.map((x) => x.code).slice(0, 8).join(', ') + (named.length > 8 ? ' …' : '');
    return {
      rank: 2,
      node: <CodeChip className="shrink-0" title={`Covers ${codes}`}>{`${named.length} ${word}${named.length === 1 ? '' : 's'}`}</CodeChip>,
      why: `Covers ${codes}${seesWholeTree ? '' : ' (of the areas you oversee)'}`,
    };
  };

  /**
   * A CANDIDATE, drawn the way every people-picker in the app draws one — identity mark, name, the
   * ROLE PILLS they hold, the territory they are based in, their number — plus the fact above. A
   * column of bare names cannot answer "which of these should work this beat?": it says who exists,
   * not who is already near it, who is free, or even which of them is a Head.
   */
  const toOption = (u: any) => {
    const fact = factFor(String(u.id));
    return {
      value: String(u.id),
      rank: fact.rank,
      ...staffOption(u, {
        // A row that already says where this person WORKS drops the home-territory chip: for the very
        // people this picker most wants to offer, the two are the same code printed twice, and the
        // weaker of the two ("based in") is the one that has to go.
        territories: fact.rank <= 1 ? undefined : terrById,
        badges: rolesOfUser(u).map((c) => <RoleBadge key={c} role={c} />),
        note: fact.node,
      }),
      title: `${staffText(u)} — ${fact.why}`,
    };
  };
  // Marked rows first — the people this territory already reaches — then alphabetical. `/users`
  // answers newest-first, which down a list of names is no order at all.
  const byFactThenName = (a: any, b: any) => (a.rank - b.rank) || a.searchLabel.localeCompare(b.searchLabel);

  // A Sales Head may only assign their own reports; Admin/SA see everyone.
  // Exclude deactivated SPs AND anyone already actively bound to THIS territory (assigning them again is a
  // backend no-op — the assign is idempotent — so offering them is just confusing).
  const assignedHere = useMemo(() => new Set((target?.assignedUserIds ?? []).map(String)), [target]);
  const spOptions = useMemo(() => {
    let list: any[] = (spUsers?.data ?? []).filter((u: any) => u.status === 'active' && !assignedHere.has(String(u.id)));
    if (myRole === 'SALES_HEAD') list = list.filter((u) => String(u.reportsTo) === String(myId));
    return list.map(toOption).sort(byFactThenName);
  }, [spUsers, myRole, myId, assignedHere, terrById, bookByUser, aboveIds, underIds]);
  const shOptions = useMemo(
    () => (shUsers?.data ?? []).filter((u: any) => u.status === 'active' && !assignedHere.has(String(u.id)))
      .map(toOption).sort(byFactThenName),
    [shUsers, assignedHere, terrById, bookByUser, aboveIds, underIds]);

  const assign = useConfirmedMutation({ title: "Assign this coverage?", content: "The user starts covering the selected territories immediately.", okText: "Yes, assign" }, {
    mutationFn: (vals: any) => request({
      url: '/territory-assignments', method: 'POST',
      // Both a Sales Person and a Sales Head may own this level; the checkbox is shown for both, so send
      // whatever it holds (only SP/SH are offered here, so no other role can slip through).
      data: { userId: vals.userId, territoryIds: [String(t.id)], isPrimary: (role === 'SALES_PERSON' || role === 'SALES_HEAD') ? !!vals.isPrimary : false },
    }),
    onSuccess: (res: any) => {
      const claimed = res?.claimed ?? 0;
      message.success(claimed ? `Assigned — ${claimed} unowned customer(s) handed over` : 'Assigned');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to assign'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={t ? `Assign to ${meta?.label} · ${t.code}` : 'Assign'}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={assign.isPending} onClick={() => form.submit()}>Assign</Button>
      </>}
    >
      {t && (
        <Form form={form} layout="vertical" onFinish={(v) => assign.mutate(v)}
          initialValues={{ role: allowSp ? 'SALES_PERSON' : 'SALES_HEAD', isPrimary: allowSp }}>
          <div style={{ marginBottom: 14 }}>
            <Tag tone={meta?.color as TagTone}>{meta?.label}</Tag> <b>{t.code}</b> · {t.name}
          </div>

          {allowSp && allowSh && (
            <Form.Item name="role" label="Bind as">
              {/* The pill is the same green / gold the person will wear on the tree once bound, so the
                  choice and the thing it produces are one colour instead of two sentences. */}
              <Select
                options={[
                  {
                    value: 'SALES_PERSON', title: 'Sales Person (works this area)', searchLabel: 'Sales Person works this area',
                    label: <span className="flex items-center gap-2"><RoleBadge role="SALES_PERSON" />Sales Person <span className="text-muted">— works this area</span></span>,
                  },
                  {
                    value: 'SALES_HEAD', title: 'Sales Head (oversees this area)', searchLabel: 'Sales Head oversees this area',
                    label: <span className="flex items-center gap-2"><RoleBadge role="SALES_HEAD" />Sales Head <span className="text-muted">— oversees this area</span></span>,
                  },
                ]}
                onChange={() => form.setFieldsValue({ userId: undefined })}
              />
            </Form.Item>
          )}

          <Form.Item name="userId" label={role === 'SALES_HEAD' ? 'Sales Head' : 'Sales Person'}
            rules={[{ required: true, message: 'Pick a user' }]}>
            {/* `searchLabel` carries the plain "Name mobile" twin — a node label has no text of its own,
                and without it typing a phone number silently matches nothing. `selectedText` keeps the
                collapsed control to mark + name: the chips make the LIST scannable; in one row they
                are only width, and width is what clips the name. */}
            <Select showSearch optionFilterProp="searchLabel" optionLabelProp="selectedText" popupMatchSelectWidth={false}
              placeholder={role === 'SALES_HEAD' ? 'Search a sales head by name or number' : 'Search a sales person by name or number'}
              notFoundContent={role === 'SALES_PERSON' && myRole === 'SALES_HEAD' ? 'No sales persons in your team' : 'None found'}
              options={role === 'SALES_HEAD' ? shOptions : spOptions} />
          </Form.Item>

          {(role === 'SALES_PERSON' || role === 'SALES_HEAD') && (
            <Form.Item name="isPrimary" valuePropName="checked"
              extra={role === 'SALES_HEAD'
                ? 'As owner, new customers here (and in its beats/districts) route to this Sales Head. Leave off to only oversee this area.'
                : 'The primary owner gets customers newly created in this territory. Leave on unless another owner is already primary here.'}>
              <Checkbox label="Make primary owner" />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}
